/**
  * @class meleView
  * @description
  *
  * # Purpose
  * Implements `<mele-view>` custom web element.
  *
  * # Use
  * Create element in HTML:
  * ```
  * parentEl.innerHTML += '<mele-view></mele-view>';
  * ```
  * Create element in JavaScript:
  * ```
  * var el = elParent.appendChildren({
  *   tag: 'mele-view',
  *   header: '${firstname} ${lastname}',
  *   footer: 'Footnote is here',
  *   ... other attributes ...
  * })
  * ```
  *
  * # Implementation notes
  *
  */

import meleContainer from 'meleContainer'
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed'

const PAGES_AROUND = 5

const BUTTON_TEMPLATES = {
  new: { iconleft: 'sys.plus', action: 'new', interclick: 0 },
  edit: { iconleft: 'u.edit', key: 'edit', interclick: 0 },
  camera: { iconleft: 'mmodPlayer', action: 'ocr', interclick: 0 },
  submit: { color: 'green', label: 'form.submit', action: 'submit', interclick: 0 },
  save: { color: 'green', label: 'form.save', action: 'submit', interclick: 0 },
  search: { color: 'green', label: 'form.search', action: 'submitvalid', interclick: 0 },
  change: { color: 'green', label: 'form.change', action: 'submit', interclick: 0 },
  overwrite: { color: 'green', label: 'form.overwrite', action: 'submit', interclick: 0 },
  ok: { color: 'green', label: 'form.ok', action: 'submit', interclick: 0 },
  okok: { color: 'green', label: 'form.ok', action: 'submitalways', interclick: 0 },
  yes: { color: 'green', label: 'form.yes', action: 'submitalways', interclick: 0 },
  no: { label: 'form.no', action: 'cancel', interclick: 0 },
  cancel: { color: 'red', label: 'form.cancel', action: 'cancel', interclick: 0 },
  clear: { color: 'red', label: 'form.clear', action: 'clear', interclick: 0 },
  undo: { label: 'form.undo', action: 'undo', interclick: 0 },
  insert: { color: 'green', label: 'form.insert', action: 'new', interclick: 0 },
  delete: { color: 'red', label: 'form.delete', action: 'delete', interclick: 0 },
  print: { key: 'print', label: 'form.print', interclick: 0 },
  refresh: { key: 'refresh', iconleft: 'wf.refresh', interclick: 0 },
  export: { key: 'export', label: 'form.export', interclick: 0 },
  import_file: { key: 'import_file', iconleft: 'server.upload.file', action: 'submitalways', interclick: 0 },
  import_folder: { key: 'import_folder', iconleft: 'server.upload.folder', action: 'submitalways', interclick: 0 },
  modalx: { iconleft: 'sys.close', action: 'modalclose', class: 'noreadonly', interclick: 0 },
  modalclose: { key: 'close', color: 'red', label: 'form.close', action: 'modalclose', interclick: 0 },
  modalcancel: { key: 'cancel', color: 'red', label: 'form.cancel', action: 'modalclose', interclick: 0 },
  back: { key: 'back', label: 'form.back', iconleft: 'arrow.w', interclick: 0 },
  next: { key: 'next', label: 'form.next', iconleft: 'arrow.e', interclick: 0 },
}

const PROPERTIES = _.extend({}, {
  unavailable: { type: 'boolean', description: 'indicates that content is unavailable' },
  blocked: { type: 'stringornull', description: 'indicates that whole module is blocked' },
  horizontal: { type: 'boolean', description: 'indicates that view shall be horizontal' },
  phoneonlyheader: { type: 'boolean', description: 'If true, header/header2/header3 will be displayed only in phone behavior' },
  vars: { type: 'string', description: 'used to store variables dependent on input fields' },
  header: { type: 'string', description: 'primary header of view' },
  header2: { type: 'string', description: 'secondary header of view' },
  header3: { type: 'string', description: 'ternary header of view' },
  headerbuttons: { type: 'array', description: 'definition of header buttons; array of buttons with either button JS definition or shortcuts "new","clear","undo","submit"' },
  headericon: { type: 'string', description: 'header icon' },
  headers: { type: 'array', description: 'getter/setter for header objects', noattribute: true },
  footer: { type: 'string', description: 'primary footer of view' },
  footer2: { type: 'string', description: 'secondary footer of view' },
  footer3: { type: 'string', description: 'ternary footer of view' },
  footerbuttons: { type: 'array', description: 'definition of header buttons; array of buttons with either button JS definition or shortcuts "new","clear","undo","submit"' },
  footers: { type: 'array', description: 'getter/setter for footer objects', noattribute: true },
  view: { type: 'boolean', description: 'indicates it is used as view; mele-window will then automatically set a role attribute' },
  role: { type: 'string', description: 'indicates role of the view; set automatically by mele-window when view attribute is true' },
  behavior: { type: 'string', description: "window's behavior", readonly: true },
  theme: { type: 'string', description: "window's theme", readonly: true },
  focusfirst: { type: 'boolean', description: 'if set true, element finds and focuses first mele-input' },
  focused: { type: 'boolean', description: 'flag if the element is in focus' },
  forcekeyboard: { type: 'boolean', description: 'flag to leave keyboard open in a view, even if we have blur event' },
  pagingoffset: { type: 'integer', description: 'offset of the first record on selected page (numbered from 0' },
  pagingsize: { type: 'integer', description: 'number of records on page' },
  pagingtotal: { type: 'integer', description: 'number of total records for paging; if empty, paging is not displayed' },
  eventPaging: { type: 'function', description: 'Called when paging offset has changed' },
  collapsible: { type: 'boolean', description: 'indicates if view can be collapsed' },
  collapsed: { type: 'boolean', description: 'indicates if view is collapsed' },
  deautoselect: { type: 'boolean', description: 'indicates if click on view should disable autoselect on sibling list' },
}, meleContainer._properties)

let blocked_template

export default class meleView extends meleContainer {
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates internal DOM structure.
  */
  init() {
    if(!blocked_template) {
      blocked_template = this.createElementFast('div')
      blocked_template.className = 'blocked'
    }

    super.init()

    // bind deautoselectHandler to myself so it can be added/removed as event listener easily
    this.deautoselectHandler = this.deautoselectHandler.bind(this)
    this.deautoselectChanged(this.deautoselect)

    const fragment = this.ownerDocument.createDocumentFragment()

    // The following mele-* elements are actually not custom elements, but just wrappers for easier styling
    // correctly, they should be just divs with classes
    // refactoring is tricky, becasuse, there may have be custom mele-* selectors expecting the current naming

    // NOT CUSTOM ELEMENTS: mele-wrapper, mele-body, mele-body-wrapper, mele-header, mele-header-wrapper, mele-footer, mele-footer-wrapper

    // insert wrapper around the whole window, putting it as the first child
    this._wrapper = this.createElementFast('mele-wrapper')
    this._wrapper.classList.add(this.classScope('wrapper'))
    fragment.appendChild(this._wrapper)

    // insert header with wrapper and ensure they are the first
    this._header_wrapper = this.createElementFast('mele-header-wrapper')
    this._header_wrapper.classList.add(this.classScope('header-wrapper'))
    this._wrapper.appendChild(this._header_wrapper)

    this._header = this.createElementFast('mele-header')
    this._header.setAttribute('key', 'core_header')
    this._header.className = 'empty'
    this._header_wrapper.appendChild(this._header)
    this._header.onclick = ()=>{
      if(this.collapsible) this.collapsed = !this.collapsed
    }

    this._header_texts = this.createElementFast('mele-header-texts')
    this._header.appendChild(this._header_texts)

    // insert body with wrapper
    this._body_wrapper = this.createElementFast('mele-body-wrapper')
    this._body_wrapper.classList.add(this.classScope('body-wrapper'))
    this._wrapper.appendChild(this._body_wrapper)
    this._body = this.createElementFast('mele-body')
    this._body.classList.add(this.classScope('body'))
    this._body_wrapper.appendChild(this._body)

    // insert footer with wrapper and ensure they are the first
    this._footer_wrapper = this.createElementFast('mele-footer-wrapper')
    this._footer_wrapper.classList.add(this.classScope('footer-wrapper'))
    this._wrapper.appendChild(this._footer_wrapper)

    this._footer = this.createElementFast('mele-footer')
    this._footer.classList.add(this.classScope('footer'))
    this._footer.setAttribute('key', 'core_footer')
    this._footer.className = 'empty'
    this._footer_wrapper.appendChild(this._footer)

    this._footer_texts = this.createElementFast('mele-footer-texts')
    this._footer_texts.classList.add(this.classScope('footer-texts'))
    this._footer.appendChild(this._footer_texts)

    this.appendChild(fragment)

    // insert paging footer
    this.updatePaging()

    // move all other already existing subelements into body
    while(this.childNodes.length>1) {
      const node = this.childNodes[0] // take first child and move it where appropriate
      const node_name = _.toLower(node.nodeName)
      if(node_name==='mele-wrapper') {
        // move all <mele-header> under mele-wrapper/mele-header-wrapper
      }else if(node_name==='mele-header') {
        // move all <mele-header> under mele-wrapper/mele-header-wrapper
        this._header_wrapper.appendChild(node)
      }else if(node_name==='mele-footer') {
        // move all <mele-footer> tag under mele-wrapper/mele-footer-wrapper
        this._footer_wrapper.appendChild(node)
      }else{
        // move all other children under mele-wrapper/mele-body-wrapper/mele-body
        this._body.appendChild(node)
      }
    }
  } // init

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handler to clear autoselect attribute from sibling mele-list element
  */
  deautoselectHandler(value) {
    const elList = this.closest('.mele-window').find('@list')
    if(elList) elList.autoselect = false
  } // deautoselectHandler

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "deautoselect"
  * Used in conjunction with "autoselect" attribute of mele-list sibling element
  */
  deautoselectChanged(value) {
    if(value && !this._deautoselect) {
      this.addEventListener('mousedown', this.deautoselectHandler)
      this.addEventListener('touchstart', this.deautoselectHandler)
      this._body_wrapper.addEventListener('scroll', this.deautoselectHandler)
      this._deautoselect = true
    }else if(!value && this._deautoselect) {
      this.removeEventListener('mousedown', this.deautoselectHandler)
      this.removeEventListener('touchstart', this.deautoselectHandler)
      this._body_wrapper.removeEventListener('scroll', this.deautoselectHandler)
      this._deautoselect = false
    }
  } // deautoselectChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "focused"
  */
  focusedChanged(value) {
    if(value) {
      if(this.focusfirst) {
        const input = this._body.find('mele-input:not([disabled]):not([readonly]):not([hidden])')
        if(!input) return
        setTimeout(()=>{
          input.inputFocus()
        }, 1)
        const scrolfn = () => {
          scrollIntoViewIfNeeded(input, true, {
            duration: 150,
          })
        }
        _.throttle(scrolfn, 200)()
      }
    } else {
      // What if multiple modules use this keyboard? we will close it for all
      this._body.forAll('mele-input[focused]', el=> { el.focused = false; el.inputBlur(true) })
    }
  } // focusedChanged

  focusinput() {
    const input = this._body.find('mele-input:not([disabled]):not([readonly]):not([hidden])')
    if(!input) return
    setTimeout(()=>{
      input.inputFocus()
    }, 1)
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "blocked"
*/
  blockedChanged(value) {
    if(typeof (value)==='string') {
      const html = this.translateAndSetDependencies(value, false, 'blocked')
      if(this._blocked) {
        this._blocked.innerHTML = html
      }else{
        this._blocked = blocked_template.cloneNode(true)
        this._blocked.innerHTML = html
        this._wrapper.appendChild(this._blocked)
      }
    }else{
      // destroy block overflow
      if(this._blocked) {
        this._blocked.deleteElement()
        delete this._blocked
      }
    }
  } // blockedChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "blocked"
  */
  unavailableChanged(value) {
    if(value) {
      if(!this._unavailable) {
        this._unavailable = this.createElementFast('mele-text')
        this._unavailable.setAttribute('key', 'unavailable')
        this._body_wrapper.insertBefore(this._unavailable, this._body_wrapper.firstChild)
      }
    }else{
      // destroy block overflow
      if(this._unavailable) {
        this._unavailable.deleteElement()
        delete this._unavailable
      }
    }
  } // unavailableChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "key"; same effect as changed language; will refresh list of items
  */
  keyChanged() {
    this.invokeDependentChanges('key')
  } // keyChanged

  roleChanged(value) {
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * set "empty" class if there is only 1 child (i.e. mele-header-texts) and all of its children are empty
  */
  _setHeaderEmptyFlag() {
    this._header.classList.toggle(
      'empty',
      (this.behavior!=='phone' && this.phoneonlyheader) ||
    (this._header.childElementCount===1 && !this._header.find('>mele-header-texts >mele-header-text:not(:empty)')),
    )
    this._header_texts.classList.toggle(
      'empty',
      !this._header_texts.find('>mele-header-text:not(:empty)'),
    )
  } // _setHeaderEmptyFlag

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "phoneonlyheader"
  */
  phoneonlyheaderChanged() {
    this._setHeaderEmptyFlag()
  } // phoneonlyheaderChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "behavior"
  */
  behaviorChanged() {
    this._setHeaderEmptyFlag()
  } // behaviorChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "vars"
  */
  varsChanged() {
    this.setAttribute('tvars', this.translateAndSetDependencies(this.vars, false, 'vars'))
  } // varsChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "header"
  */
  headerChanged() {
    const html = this.translateAndSetDependencies(this.header, false, 'header')
    const el = this._header_texts.find('>mele-header-text:nth-of-type(1)') || this._header_texts.appendChildren({ tag: 'mele-header-text' })
    if(el.innerHTML !== html) el.innerHTML = html
    this._setHeaderEmptyFlag()
  } // headerChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "header2"
  */
  header2Changed() {
    let html = this.translateAndSetDependencies(this.header2, false, 'header2')
    const el = this._header_texts.find('>mele-header-text:nth-of-type(2)') || this._header_texts.appendChildren({ tag: 'mele-header-text' })
    html = html.replace(/^\s+/, '').replace(/\s+$/, '')
    if(el.innerHTML !== html) el.innerHTML = html
    this._setHeaderEmptyFlag()
  } // header2Changed

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "header3"
  */
  header3Changed() {
    const html = this.translateAndSetDependencies(this.header3, false, 'header3')
    const el = this._header_texts.find('>mele-header-text:nth-of-type(3)') || this._header_texts.appendChildren({ tag: 'mele-header-text' })
    if(el.innerHTML !== html) el.innerHTML = html
    this._setHeaderEmptyFlag()
  } // header2Changed

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "headericon"
  */
  headericonChanged() {
    const icon = this.translateAndSetDependencies(this.headericon, false, 'headericon')
    let el = this._header.find('>mele-header-icon')
    if(icon) {
      el = el || this._header.prependChildren({ tag: 'mele-header-icon' })
      el.innerHTML = ''
      el.appendChildren({
        tag: 'mele-image',
        icon,
      })
    }else{
      if(el) el.deleteElement()
    }
    this._setHeaderEmptyFlag()
  } // headericonChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "headerbuttons"
  */
  headerbuttonsChanged() {
    this._header.forAll('>*', el=>{
      if(_.toLower(el.tagName)!=='mele-header-texts') { el.deleteElement() }
    })
    this.headerbuttons.forEach(button=>{
      if(typeof (button)==='string') { button = BUTTON_TEMPLATES[button] }
      if(_.isObjectLike(button)) {
        button = _.extend({ tag: 'mele-button', type: 'toolbar' }, button)
        this._header.prependChildren(button, this._header_texts)
      }
    })
    this._setHeaderEmptyFlag()
  } // headerbuttonsChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * set "empty" class if there is only 1 child (i.e. mele-footer-texts) and all of its children are empty
  */
  _setFooterEmptyFlag() {
    this._footer.classList.toggle(
      'empty',
      this._footer.childElementCount===1 && !this._footer.find('>mele-footer-texts >mele-footer-text:not(:empty)'),
    )
    this._footer_texts.classList.toggle(
      'empty',
      !this._footer_texts.find('>mele-footer-text:not(:empty)'),
    )
  } // _setFooterEmptyFlag

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "footer"
  */
  footerChanged() {
    const html = this.translateAndSetDependencies(this.footer, false, 'footer')
    const el = this._footer_texts.find('>mele-footer-text:nth-of-type(1)') || this._footer_texts.appendChildren({ tag: 'mele-footer-text' })
    if(el.innerHTML !== html) el.innerHTML = html
    this._setFooterEmptyFlag()
  } // footerChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "footer2"
  */
  footer2Changed() {
    const html = this.translateAndSetDependencies(this.footer2, false, 'footer2')
    const el = this._footer_texts.find('>mele-footer-text:nth-of-type(2)') || this._footer_texts.appendChildren({ tag: 'mele-footer-text' })
    if(el.innerHTML !== html) el.innerHTML = html
    this._setFooterEmptyFlag()
  } // footer2Changed

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "footer2"
  */
  footer3Changed() {
    const html = this.translateAndSetDependencies(this.footer3, false, 'footer3')
    const el = this._footer_texts.find('>mele-footer-text:nth-of-type(3)') || this._footer_texts.appendChildren({ tag: 'mele-footer-text' })
    if(el.innerHTML !== html) el.innerHTML = html
    this._setFooterEmptyFlag()
  } // footer2Changed

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "footerbuttons"
  */
  footerbuttonsChanged() {
    this._footer.forAll('>*', el=>{
      if(_.toLower(el.tagName)!=='mele-footer-texts') { el.deleteElement() }
    })
    this.footerbuttons.forEach(button=>{
      if(typeof (button)==='string') { button = BUTTON_TEMPLATES[button] }
      if(_.isObjectLike(button)) {
        button = _.extend({ tag: 'mele-button', type: 'actionbar' }, button)
        this._footer.prependChildren(button, this._footer_texts)
      }
    })
    this._setFooterEmptyFlag()
    this.formStatusChanged()
  } // footerbuttonsChanged

  headersRetrieve() {
    if(!this._header_wrapper) return []
    return this._header_wrapper.findAll('>mele-header:not(@core_header)').map(el=>el.js)
  }

  headersStore(value) {
    if(!this._header_wrapper) return
    this._header_wrapper.forAll('>mele-header:not(@core_header)', el=>el.deleteElement())
    value = Array.isArray(value) ? value : []
    value.forEach(header=>this.appendHeader(header))
  }

  appendHeader(json) {
    json = _.extend({ tag: 'mele-header' }, json)
    return this._header_wrapper.appendChildren(json)
  }

  footersRetrieve() {
    if(!this._footer_wrapper) return []
    return this._footer_wrapper.findAll('>mele-footer:not(@core_footer)').map(el=>el.js)
  }

  footersStore(value) {
    if(!this._footer_wrapper) return
    this._footer_wrapper.forAll('>mele-footer:not(@core_footer)', el=>el.deleteElement())
    value = Array.isArray(value) ? value : []
    value.forEach(footer=>this.appendFooter(footer))
  }

  appendFooter(json) {
    json = _.extend({ tag: 'mele-footer' }, json)
    return this._footer_wrapper.appendChildren(json)
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "pagingoffset"
  */
  pagingoffsetChanged() {
    this.updatePaging()
  } // pagingoffsetChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "pagingsize"
  */
  pagingsizeChanged() {
    this.updatePaging()
  } // pagingsizeChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "pagingtotal"
  */
  pagingtotalChanged() {
    this.updatePaging()
  } // pagingtotalChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Updates paging footer
  */
  updatePaging() {
    const total = parseInt(this.pagingtotal)
    let offset = _.natural(this.pagingoffset)
    if(offset>=total) offset = total>0 ? total-1 : 0
    const pagesize = _.natural(this.pagingsize)
    if(total>=0 && pagesize>0) {
      this.setPaging()
      const summary = total>0 ? `${offset+1}-${_.min([offset+pagesize, total])} {{paging.of}} ${total}` : '{{paging.no_results}}'
      this._paging_summary.label = this.translateAndSetDependencies(summary, false, 'pagingtotal') // translate summary and ensure it will be updated if language changes

      const page_selected = parseInt(offset / pagesize) + 1
      const page_count = parseInt((total-1) / pagesize) + 1
      const first_page = _.max([1, page_selected-PAGES_AROUND])
      const last_page = _.min([page_count, page_selected+PAGES_AROUND])
      this._paging_pages.innerHTML = ''
      if(total>0) {
        for(let i=first_page; i<=last_page; i++) {
          this._paging_pages.appendChildren({
            tag: 'mele-button',
            key: i,
            label: i,
            type: 'paging',
            selected: i===page_selected,
            eventButtonClicked: (el)=>{
              this.pagingClicked(el.key)
            },
          })
        }
      }

      this._paging_first.disabled = total===0
      this._paging_prev.disabled = page_selected===1
      this._paging_next.disabled = page_selected>=page_count
      this._paging_last.disabled = total===0

      this._paging.classList.toggle('empty', false)
    }else{
      if(this._paging) {
        this._paging.innerHTML = ''
        this._paging.classList.toggle('empty', true)
      }
    }
  } // updatePaging

  setPaging() {
    if(this._paging) this._paging.innerHTML = ''
    this._paging = this._footer_wrapper.appendChildren({ tag: 'mele-footer', key: 'paging_footer', class: 'empty' }, this._footer)
    this._paging_summary = this._paging.appendChildren({ tag: 'mele-text', key: 'paging_summary' })
    this._paging_buttons = this._paging.appendChildren({ tag: 'div', key: 'paging_buttons' })
    this._paging_first = this._paging_buttons.appendChildren({
      tag: 'mele-button',
      type: 'paging',
      iconleft: 'page.first',
      eventButtonClicked: ()=>{ this.pagingClicked(1) },
    })
    this._paging_prev = this._paging_buttons.appendChildren({
      tag: 'mele-button',
      type: 'paging',
      iconleft: 'page.prev',
      eventButtonClicked: ()=>{ this.pagingClicked(parseInt(this.pagingoffset/this.pagingsize)) },
    })
    this._paging_pages = this._paging_buttons.appendChildren({ tag: 'div', key: 'paging_pages' })
    this._paging_next = this._paging_buttons.appendChildren({
      tag: 'mele-button',
      type: 'paging',
      iconleft: 'page.next',
      eventButtonClicked: ()=>{ this.pagingClicked(parseInt(this.pagingoffset/this.pagingsize)+2) },
    })
    this._paging_last = this._paging_buttons.appendChildren({
      tag: 'mele-button',
      type: 'paging',
      iconleft: 'page.last',
      eventButtonClicked: ()=>{ this.pagingClicked(parseInt((this.pagingtotal-1)/this.pagingsize)+1) },
    })
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * handle click on paging button
  */
  pagingClicked(page) {
    page = _.natural(page) || 1
    const total = parseInt(this.pagingtotal)
    const pagesize = _.natural(this.pagingsize)
    if(total>0 && pagesize>0) {
      const page_count = parseInt((total-1) / pagesize) + 1
      if(page>page_count) page = page_count
      try{
        this.eventPaging(this, (page-1)*pagesize)
      }catch(e) {}
    }
  } // pagingClicked

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Default handling of clicked paging
  * Could be overwritten.
  */
  eventPaging(self, pagingoffset) {
    this.publish('mmViewPaging', { holder: this.key, offset: _.natural(pagingoffset) })
  } // eventPaging

  // scroll to top
  scrollTop() {
    this._body_wrapper.scrollTop = 0
  }

  scrollBottom() {
    this._body_wrapper.scrollTop = this._body_wrapper.scrollHeight
  }

  eventFocused() {

  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of items
  */
  coerceItem(json) {
    if(typeof (json)==='string' || typeof (json)==='number') {
      // string definition is a shortcut for mele-text
      json = { tag: 'mele-text', label: _.toString(json) }
    }
    if(_.isObjectLike(json)) {
      json = _.extend({
        tag: 'mele-input',
        type: 'string',
      }, json)
      return json
    }else{
      return null
    }
  }

  collapsedChanged(value, old) {
    if(old && !value) {
      this.scrollIntoView({ behavior: 'smooth', block: 'start' })
      // this.scrollTop = this.scrollHeight;
    }
  }
} // class meleView

meleView._properties = PROPERTIES
meleView.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-view', meleView)
